import { Dialog as MuiDialog } from "@mui/material";

import { Box } from "ui/box/box";
import { CloseButton } from "ui/close-button/close-button";
import { Typography } from "ui/typography/typography";
import { mergeSx } from "ui/merge-sx";

import { useDialog } from "ui/dialog/use-dialog";

import { getDialogPortalContainer } from "ui/dialog/dialog.helpers";

import { STATUS_GLYPH } from "ui/dialog/dialog.constants";
import {
  ACTIONS_SX,
  BODY_SCROLL_SX,
  BODY_SX,
  CLOSE_BUTTON_SX,
  CONTENT_SX,
  DESCRIPTION_SX,
  GLYPH_SX_BY_INTENT,
  HEADLINE_ICON_SX,
  HEADLINE_SX,
  HEADER_SX,
  STEP_INDICATOR_SX,
  PAPER_SX,
  PAPER_SX_BY_INTENT,
  PAPER_SX_BY_SIZE,
  getPaperWidthSx,
} from "ui/dialog/dialog.styles";
import { type DialogProps } from "ui/dialog/dialog.types";

export const Dialog = ({
  children,
  open,
  closable = true,
  onClose,
  size = "medium",
  width,
  intent = "inherit",
  icon,
  headline,
  badge,
  step,
  description,
  actions,
  closeOnBrowserBack = true,
  "aria-label": ariaLabel,
  "aria-labelledby": ariaLabelledby,
  "aria-describedby": ariaDescribedby,
  ...rest
}: DialogProps) => {
  const { headlineId, descriptionId, statusIntent, handleClose } = useDialog({
    open,
    onClose,
    closable,
    closeOnBrowserBack,
    intent,
  });

  return (
    <MuiDialog
      {...rest}
      open={open}
      onClose={handleClose}
      /* Without a headline MUI still points this at an id nothing carries; the caller's own
         label, if any, is the only one left to use. */
      aria-labelledby={headline ? headlineId : ariaLabelledby}
      aria-describedby={description ? descriptionId : ariaDescribedby}
      container={getDialogPortalContainer()}
      maxWidth={false}
      slotProps={{
        paper: {
          /* `role="dialog"` sits on the paper, not the root `...rest` reaches. */
          "aria-label": ariaLabel,
          sx: mergeSx(
            PAPER_SX,
            PAPER_SX_BY_INTENT[intent],
            PAPER_SX_BY_SIZE[size],
            getPaperWidthSx(width),
          ),
        },
      }}>
      {closable && <CloseButton sx={CLOSE_BUTTON_SX} onClick={onClose} />}

      {statusIntent && (
        <Box
          component={STATUS_GLYPH[statusIntent]}
          aria-hidden
          data-testid="dialog-status-icon"
          sx={GLYPH_SX_BY_INTENT[statusIntent]}
        />
      )}

      <Box sx={CONTENT_SX}>
        {headline && (
          <Box sx={HEADER_SX}>
            <Typography id={headlineId} variant="h2" component="h2" sx={HEADLINE_SX}>
              {icon && (
                <Box component="span" aria-hidden sx={HEADLINE_ICON_SX}>
                  {icon}
                </Box>
              )}
              {headline}
              {badge}
            </Typography>
            {step && (
              <Typography variant="subtitle1" component="p" sx={STEP_INDICATOR_SX}>
                Step {step.current} of {step.total}
              </Typography>
            )}
          </Box>
        )}

        {description && (
          <Typography id={descriptionId} variant="body1" component="div" sx={DESCRIPTION_SX}>
            {description}
          </Typography>
        )}

        {children && (
          <Box
            data-testid="dialog-body"
            sx={mergeSx(
              BODY_SX,
              size !== "small" ? BODY_SCROLL_SX : undefined,
            )}>
            {children}
          </Box>
        )}

        {actions && <Box sx={ACTIONS_SX}>{actions}</Box>}
      </Box>
    </MuiDialog>
  );
};
